/**
 * ATPL Primary Button Component
 * 
 * A customized Material UI button component that follows ATPL design guidelines.
 * This component provides a consistent look and feel across the application
 * with proper styling for normal, hover, and active states.
 * 
 * Features:
 * - Uses ATPL brand color (#F15C30)
 * - Custom shadow effects for normal and hover/active states
 * - Accepts dynamic text content
 * - Responsive font sizing across breakpoints
 * - Fully customizable with MUI props and sx prop support
 * 
 * @module AtplPrimaryBtnNew
 * @author ATPL Design Team
 * @version 1.0.0
 */

import React from 'react';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';

// Custom styled button based on MUI Button with ATPL design specifications
const CustomButton = styled( Button )( ( { theme } ) => ( {
  backgroundColor: '#F15C30',
  color: 'white',
  fontWeight: '600',
  padding: '12px 24px',
  borderRadius: '8px',
  textTransform: 'none',
  boxShadow: '0px 35px 35px 0px rgba(0, 0, 0, 0.09), 0px 9px 19px 0px rgba(0, 0, 0, 0.10)',
  transition: 'all 0.3s ease',
  position: 'relative',
  border: 'none',
  // Responsive font sizes using proper MUI theme breakpoints
  [ theme.breakpoints.up( 'xs' ) ]: {
    fontSize: '16px',
  },
  [ theme.breakpoints.up( 'sm' ) ]: {
    fontSize: '18px',
  },
  [ theme.breakpoints.up( 'md' ) ]: {
    fontSize: '20px',
  },
  '&:hover': {
    backgroundColor: '#F15C30', // Keep the same color on hover
    boxShadow: '0px 8px 20px 0px rgba(0, 0, 0, 0.25), 0px 35px 35px 0px rgba(0, 0, 0, 0.09)'
  },
  '&:active': {
    backgroundColor: '#F15C30', // Keep the same color on active
    boxShadow: '0px 25px 35px 0px rgba(0, 0, 0, 0.25)',
  },
  '&:focus': {
    boxShadow: '0px 25px 35px 0px rgba(0, 0, 0, 0.25)',
  },
} ) );

/**
 * AtplPrimaryBtnNew Component
 * 
 * @param {Object} props - Component props
 * @param {string} [props.text="Next"] - Button text content
 * @param {function} [props.onClick] - Click event handler
 * @param {Object} [props.sx] - Additional sx styles to apply
 * @returns {React.Component} - Styled button component
 * 
 * @example
 * <AtplPrimaryBtnNew text="Submit" onClick={handleSubmit} />
 */
const AtplPrimaryBtnNew = ( { text = "Next", onClick, ...props } ) => {
  return (
    <CustomButton
      variant="contained"
      onClick={ onClick }
      disableRipple
      disableElevation
      { ...props }
    >
      { text }
    </CustomButton>
  );
};

export default AtplPrimaryBtnNew;